import * as React from 'react'
import { type SelectChangeEvent } from '@mui/material/Select'
import { useNavigate } from 'react-router-dom'
import Link from '@mui/material/Link'
import { type SnackbarOrigin } from '@mui/material/Snackbar'
import Typography from '@mui/material/Typography'
import EnhancedTable from './KpiTable'
import { type ConnectedProps, connect, useSelector } from 'react-redux'
import { type RootState } from '../../../store'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import ArrowBackIos from '@mui/icons-material/ArrowBackIos'
import LanguageStrings from '../../../i18n/locales'
import { Button } from '../../../components/Button'
import SearchBox from '../../../components/SearchBox'
import { useAuth } from 'react-oidc-context'
import { type Sort } from './KpiTable'
import { type OperationPermissionState } from '../../dashboard/redux/operationPermissionSlice'
import { getOperationPermissions } from '../../dashboard/redux/actionCreator'
import { fetchKpiBySearchValue, fetchKpiList } from '../redux/actionCreators'
import { type KpiListState } from '../redux/kpiListSlice'
import { Grid } from '@mui/material'

export interface State extends SnackbarOrigin {
  open: boolean
}
const KpiStrings = LanguageStrings().ThermalIQStrings

function EnhancedTableToolbar (props: any): JSX.Element {
  const { numSelected } = props
  return (

        <div>
            {numSelected > 0
              ? (
                <Typography
                    sx={{ flex: '1 1 100%', paddingLeft: '16px', fontFamily: 'Honeywell Sans Web' }}
                    color='inherit'
                    variant='subtitle1'
                    component='div'
                >
                    {numSelected} {KpiStrings.CustomKpiListComponent.selected}
                </Typography>
                )
              : (
                <div className='configurations-table-heading'>
                    <Typography color='inherit' sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '16px', fontWeight: '700', lineHeight: '24px', letterSpacing: '2.5px' }}>{KpiStrings.CustomKpiListComponent.CustomKpi}</Typography>
                </div>
                )}
        </div>
  )
}

const style: React.CSSProperties = {
  width: '99%',
  backgroundColor: '#272727',
  border: '1px solid #101010',
  borderRadius: '10px',
  boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.15)',
  marginBottom: '16px',
  padding: 0,
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0
}

function CustomKpiList (props: PropsFromRedux): JSX.Element {
  const DashboardStrings = LanguageStrings().Dashboard
  const [selected] = React.useState<readonly string[]>([])
  const [sortCondition, setSortCondition] = React.useState('')
  const [searchValue, setSearchValue] = React.useState('')
  const rowsPerPage = KpiStrings.CustomKpiListComponent.RowPerPage
  const [page, setPage] = React.useState<number>(1)
  const [sort, setSort] = React.useState<Sort>({ order: 'desc', orderBy: 'createdAt' })
  const operationPermissions: OperationPermissionState = useSelector((state: RootState) => state.operationPermissions)
  const auth = useAuth()

  const navigate = useNavigate()

  const handleChange = (event: SelectChangeEvent): void => {
    setSortCondition(event.target.value)
  }

  /* istanbul ignore next */
  const handleSearch = (): void => {
    setPage(1)
    props.fetchKpiBySearchValue(searchValue, true, 0, rowsPerPage, sort.orderBy, sort.order)
  }

  function handleBreadCrumbsClick (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    event.preventDefault()
  }

  React.useEffect(() => {
    (!operationPermissions.updated && props.getOperationPermissions(auth.user?.profile.sub as string))
  }, [])

  return (
        <div>
          <div role='presentation' data-testid='breadcrumbs' onClick={handleBreadCrumbsClick} className='AmiHeading' >
            <ArrowBackIos className="assetmgmtli" data-testid="breadcrumb-arrow-back" aria-label="navArrow" sx={{ marginTop: '10px' }}
              onClick={() => { navigate(-1) }}
            />
            <Breadcrumbs aria-label='breadcrumb' sx={{ color: '#f0f0f0', fontSize: '12px', marginBottom: '14px', fontFamily: 'Honeywell Sans Web', marginTop: '12px' }}>
              <Link aria-label='navBreadcrumb' className='assetmgmtli' data-testid="bc-link-1" underline='none' color='inherit' onClick={() => { navigate('/thermal-dashboard') }} >
                {DashboardStrings.HOME}
              </Link>
              <Typography color='inherit' sx={{ fontSize: '12px', fontFamily: 'Honeywell Sans Web' }}>{KpiStrings.CustomKpiListComponent.KpiConfiguration}</Typography>
            </Breadcrumbs>
                        </div>
              <div className='ArrowDeviceList'>
                  <div>
                      <span data-testid='deviceHistoryDetails' className='DevicelistDeviceComponent'>{KpiStrings.CustomKpiListComponent.KpiConfiguration}</span>
                      <span className='TotalDeviceComponent'> {KpiStrings.CustomKpiListComponent.Total} <b>{props.kpiList.kpiCount}</b> {KpiStrings.CustomKpiListComponent.Configurations}</span>
                  </div>
              </div>
          <EnhancedTableToolbar numSelected={selected.length} handleChange={handleChange}
                          sortCondition={sortCondition} kpiList={props.kpiList.kpiList}
                          kpiCount={props.kpiList.kpiCount}
                      />
          <Grid container style={style} justifyContent={'space-between'} >
            <Grid item xs={6} sm={6} md={4} lg={3} >
              <SearchBox
                sx={{ width: '100%', paddingRight: '0px' }}
                containerStyle={{ width: '100%' }}
                immediate={true} placeholder='Search'
                emptyEnterFallback={() => { props.fetchKpiList(0, 10, 'desc', 'createdAt', true) }}
                searchVal={searchValue} setSearchVal={setSearchValue}
                performAction={handleSearch} />
            </Grid>
            <Grid item xs={6} sm={6} md={4} lg={3} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <>
                 {
                    operationPermissions.permissions.can_create_tiq_kpis
                      ? <Button sx={{ margin: '10px 17px', padding: '1em', display: 'flex', alignItems: 'center' }} width='fit-content' onClick={() => { navigate('/create-custom-kpi') }} dataTestId='import-asset-list-btn'>{KpiStrings.CustomKpiListComponent.CreateCustomKpi}</Button>
                      : <></>
                  }
              </>
            </Grid>
           <Grid item xs={12}>
            <EnhancedTable searchVal={searchValue} page={page} rowsPerPage={rowsPerPage} sort={sort} setPage={setPage} setSort={setSort} performAction={() => { props.fetchKpiBySearchValue(searchValue, true, page - 1, rowsPerPage, sort.orderBy, sort.order) }}/>
           </Grid>
          </Grid>
        </div>
  )
}

interface DispatchToProps {
  fetchKpiList: (page: number, size: number, sortdir: string, sortfield: string, filter: boolean) => void
  fetchKpiBySearchValue: (searchVal: string, filterVal: boolean, page: number, size: number, sortfield: string, sortdir: string) => void
  getOperationPermissions: (sub: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchKpiList: (page: number, size: number, sortdir: string, sortfield: string, filter: boolean) => dispatch(fetchKpiList(page, size, sortdir, sortfield, filter)),
  fetchKpiBySearchValue: (searchVal: string, filterVal: boolean, page: number, size: number, sortfield: string, sortdir: string) => dispatch(fetchKpiBySearchValue(searchVal, filterVal, page, size, sortfield, sortdir)),
  getOperationPermissions: (sub: string) => dispatch(getOperationPermissions(sub))
})

interface StateToProps {
  kpiList: KpiListState
  grandParentBreadcrumb: string
}

const mapStateToProps = (state: RootState): StateToProps => ({
  kpiList: state.kpiList,
  grandParentBreadcrumb: state.grandParentBreadcrumb
})
const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(CustomKpiList)
